import { useState, useMemo, useEffect, useCallback } from 'react';
import { SIZE_OPTIONS, LENGTH_OPTIONS } from '../constants/serviceOptions';
import type { PricingCombination } from '../types/appointments';
import type {
	PublicBookingAddOn,
	PublicBookingService,
	SelectedAddOn,
} from '../types/publicBooking';

/** An add-on is either off the basket or on it at least once. */
export const ADD_ON_MIN_QUANTITY = 1;

/** Ceiling the API enforces per add-on; the stepper stops here too. */
export const ADD_ON_MAX_QUANTITY = 10;

/** Services that come back with no category are grouped under this heading. */
export const OTHERS_CATEGORY = 'Others';

/** Pseudo-category for the "show everything" filter pill. */
export const ALL_CATEGORIES = 'All';

export interface ServiceCostLine {
	id: number;
	name: string;
	price: number;
	durationMinutes: number;
	/**
	 * False while a size or length the service is priced by is still unpicked
	 * (or can't be picked because the basket clashes). `price` is then the
	 * cheapest row the choice could still land on — a "from" figure.
	 */
	priced: boolean;
}

const categoryOf = (service: PublicBookingService): string =>
	service.category?.trim() || OTHERS_CATEGORY;

/**
 * Orders a set of size/length codes by the canonical catalogue order so the
 * pills always read S → M → L rather than in whatever order the API returned
 * the pricing rows. Codes outside the catalogue keep their relative order and
 * are appended.
 */
const orderByCatalogue = (values: Set<string>, catalogue: string[]): string[] => {
	const known = catalogue.filter(v => values.has(v));
	const unknown = [...values].filter(v => !catalogue.includes(v));
	return [...known, ...unknown];
};

/** Reads a pricing code: null, undefined and whitespace all mean "not priced by this". */
const codeOf = (value: string | null | undefined): string => (value ?? '').trim();

/**
 * Reads a price or a duration. Both are mandatory on every pricing row, so this
 * only catches a row that arrives without one: left alone, a null or a NaN
 * spreads through the total and reaches the customer as "$NaN", which is worse
 * than a wrong figure because there is nothing to act on.
 */
const finite = (value: number | null | undefined): number =>
	typeof value === 'number' && Number.isFinite(value) ? value : 0;

/**
 * Whether a pricing row applies to what the customer picked. A blank code on
 * the row is a wildcard — an unsized service prices every size — and a blank
 * pick matches every row. Used for narrowing the option lists and for the
 * last-resort estimate in `resolveCombination`, not for pricing a normal
 * booking: the ladder below is stricter and is what the server does.
 */
const appliesTo = (rowCode: string, picked: string): boolean =>
	!rowCode || !picked || rowCode === picked;

/** Lowest-priced row of a set, or null for an empty one. */
const cheapest = (rows: PricingCombination[]): PricingCombination | null =>
	rows.reduce<PricingCombination | null>(
		(best, row) => (best === null || row.price < best.price ? row : best),
		null
	);

/** The sizes a service is actually priced at. Empty means the service isn't sized. */
const sizesOf = (service: PublicBookingService): Set<string> =>
	new Set(service.pricingCombinations.map(pc => codeOf(pc.size)).filter(Boolean));

/** As `sizesOf`, for lengths. */
const lengthsOf = (service: PublicBookingService): Set<string> =>
	new Set(service.pricingCombinations.map(pc => codeOf(pc.length)).filter(Boolean));

/**
 * The row a service is priced by, most specific first: the exact size/length
 * pair, then the size on its own, then the length on its own, then the
 * catch-all row that prices everything. Price and duration both come from
 * whichever row wins.
 *
 * This mirrors the server's resolver, verified against the API on 2026-09-27
 * with a service carrying all four row shapes. Two properties are worth
 * spelling out because they are not obvious and a cheaper-looking row makes
 * them easy to get wrong:
 *
 *  - an exact row wins even when a wildcard row is cheaper (SM + BRA resolved
 *    to the $40 exact row, not the $10 catch-all);
 *  - size outranks length, so a pair with no exact row falls back to the size
 *    row rather than the length row (XXS + BRA resolved to XXS/– at $50, not
 *    –/BRA at $30).
 *
 * A field the customer was never asked for is blank here, which lines up with
 * the null the server sees: the steps naming it then only match rows that are
 * themselves blank, exactly as the server's do.
 */
const resolveCombination = (
	service: PublicBookingService,
	size: string,
	length: string
): PricingCombination | null => {
	const rows = service.pricingCombinations;
	const pricedAt = (wantSize: string, wantLength: string) =>
		cheapest(
			rows.filter(pc => codeOf(pc.size) === wantSize && codeOf(pc.length) === wantLength)
		);

	return (
		pricedAt(size, length) ??
		pricedAt(size, '') ??
		pricedAt('', length) ??
		pricedAt('', '') ??
		// Nothing on the ladder fits only when a field the service prices by was
		// left unset — the basket whose services share no size, where there is
		// nothing for the customer to pick. Quote the cheapest row it could turn
		// out to be rather than showing nothing; the page says the stylist will
		// confirm, and the server's own figures replace this once the booking is
		// created.
		cheapest(
			rows.filter(
				pc => appliesTo(codeOf(pc.size), size) && appliesTo(codeOf(pc.length), length)
			)
		)
	);
};

/** Intersects the per-service option sets, ordered by the canonical catalogue. */
const sharedOptions = (perService: Set<string>[], catalogue: string[]): string[] => {
	if (perService.length === 0) return [];
	const shared = new Set(
		[...perService[0]].filter(candidate => perService.every(set => set.has(candidate)))
	);
	return orderByCatalogue(shared, catalogue);
};

/**
 * The lengths every length-priced service offers at `atSize`, so anything
 * returned can be booked for the whole basket rather than for part of it. A
 * blank `atSize` asks across every size, which is what an unanswered size
 * question amounts to.
 */
const lengthsAt = (services: PublicBookingService[], atSize: string): string[] =>
	sharedOptions(
		services.map(
			service =>
				new Set(
					service.pricingCombinations
						.filter(pc => appliesTo(codeOf(pc.size), atSize))
						.map(pc => codeOf(pc.length))
						.filter(Boolean)
				)
		),
		LENGTH_OPTIONS
	);

/**
 * Drives the service → size / length selection shared by both public booking
 * pages.
 *
 * Not every service is priced by size and length: a consultation or a wash
 * carries a single combination with both left blank, which is why the create
 * API takes the fields as optional. So the two questions are asked only when
 * something in the basket is priced by them, and answered whenever they are
 * asked. That, plus `resolveCombination` walking the same ladder the server
 * walks, is what keeps the total quoted here identical to the one charged.
 *
 * The API takes many `serviceIds` but a single `size` and `length`, so the
 * options offered are the ones every service that prices by that field can
 * actually be booked at (the intersection of their pricing combinations).
 */
export const usePublicBookingSelection = (
	services: PublicBookingService[],
	addOns: PublicBookingAddOn[] = []
) => {
	const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORIES);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	// An array rather than an id→quantity map so the basket keeps the order the
	// customer built it in — object keys that look like numbers would re-sort
	// themselves numerically and shuffle the summary lines.
	const [addOnLines, setAddOnLines] = useState<{ id: number; quantity: number }[]>([]);
	const [size, setSize] = useState('');
	const [length, setLength] = useState('');

	// ── Categories ──
	const categories = useMemo(() => {
		const seen: string[] = [];
		services.forEach(s => {
			const cat = categoryOf(s);
			if (!seen.includes(cat)) seen.push(cat);
		});
		// "Others" always sits last, however the API ordered the services.
		const ordered = seen.filter(c => c !== OTHERS_CATEGORY);
		if (seen.includes(OTHERS_CATEGORY)) ordered.push(OTHERS_CATEGORY);
		return [ALL_CATEGORIES, ...ordered];
	}, [services]);

	const selectedServices = useMemo(
		() => selectedIds.map(id => services.find(s => s.id === id)).filter(Boolean),
		[selectedIds, services]
	) as PublicBookingService[];

	// ── Size / length options ──
	// Only services actually priced by a field get a say in its options: a wash
	// sharing the basket with braids must not empty the size pills just because
	// the wash has no sizes of its own.
	const sizedServices = useMemo(
		() => selectedServices.filter(service => sizesOf(service).size > 0),
		[selectedServices]
	);

	const lengthedServices = useMemo(
		() => selectedServices.filter(service => lengthsOf(service).size > 0),
		[selectedServices]
	);

	// Size and length travel together: a pricing row carries both codes or
	// carries neither, so a size is only worth offering if the basket can also be
	// given a length at it. Filtering the dead ones out surfaces a clash here, on
	// the size pills, rather than letting the customer pick a size and then meet
	// an empty length row with nothing to do about it.
	const sizeOptions = useMemo(() => {
		const shared = sharedOptions(sizedServices.map(sizesOf), SIZE_OPTIONS);
		if (lengthedServices.length === 0) return shared;
		return shared.filter(candidate => lengthsAt(lengthedServices, candidate).length > 0);
	}, [sizedServices, lengthedServices]);

	// Narrowed to the chosen size, since a service need not offer every length at
	// every size. A service left with nothing at that size keeps its empty set
	// here rather than dropping out — that clash has to reach the customer.
	const lengthOptions = useMemo(
		() => lengthsAt(lengthedServices, size),
		[lengthedServices, size]
	);

	/** Whether anything in the basket is priced by the field. If not, don't ask. */
	const sizeApplies = sizedServices.length > 0;
	const lengthApplies = lengthedServices.length > 0;

	/**
	 * Asked, but with nothing left to answer: the services priced by a field
	 * share no value for it. The two questions fall together, because a row
	 * carries both codes or carries neither — a length answered beside a size
	 * that cannot be answered has no row to land on, and sending it alone asks
	 * the server to price a combination it has no row for.
	 */
	const groupClashes =
		(sizeApplies && sizeOptions.length === 0) || (lengthApplies && lengthOptions.length === 0);

	// Adding or removing a service can invalidate an earlier pick — drop it
	// rather than silently submitting a size the new service isn't priced for,
	// or a length whose partner size has just become unanswerable.
	useEffect(() => {
		if (size && (groupClashes || !sizeOptions.includes(size))) setSize('');
	}, [size, sizeOptions, groupClashes]);

	useEffect(() => {
		if (length && (groupClashes || !lengthOptions.includes(length))) setLength('');
	}, [length, lengthOptions, groupClashes]);

	// ── Data guarantees ──
	// Two things have to hold for the figure quoted here to be the one the server
	// charges: a pricing row carries size and length together or carries neither,
	// and every row has a price and a duration. Neither is something this page can
	// enforce, so a row that breaks one says so loudly in development instead of
	// surfacing later as a deposit that does not match the invoice.
	useEffect(() => {
		if (!import.meta.env.DEV) return;
		services.forEach(service =>
			service.pricingCombinations.forEach(pc => {
				if (!!codeOf(pc.size) !== !!codeOf(pc.length)) {
					// eslint-disable-next-line no-console
					console.warn(
						`[booking] "${service.name}" has a pricing row carrying only one of ` +
							`size/length (size=${pc.size ?? 'null'}, length=${pc.length ?? 'null'}). ` +
							'Rows must carry both or neither.'
					);
				}
				if (finite(pc.price) !== pc.price) {
					// eslint-disable-next-line no-console
					console.warn(`[booking] "${service.name}" has a row with no price.`);
				}
				if (finite(pc.durationMinutes) !== pc.durationMinutes) {
					// eslint-disable-next-line no-console
					console.warn(`[booking] "${service.name}" has a row with no duration.`);
				}
			})
		);
	}, [services]);

	// ── Pricing ──
	const costLines = useMemo<ServiceCostLine[]>(() => {
		return selectedServices.map(service => {
			const combo = resolveCombination(service, size, length);
			const needsSize = sizesOf(service).size > 0;
			const needsLength = lengthsOf(service).size > 0;
			return {
				id: service.id,
				name: service.name,
				price: finite(combo?.price),
				durationMinutes: finite(combo?.durationMinutes),
				priced: !groupClashes && (!needsSize || !!size) && (!needsLength || !!length),
			};
		});
	}, [selectedServices, size, length, groupClashes]);

	const serviceOnlyTotal = useMemo(
		() => costLines.reduce((total, line) => total + line.price, 0),
		[costLines]
	);

	// ── Add-ons ──
	const selectedAddOns = useMemo(
		() =>
			addOnLines
				.map(line => {
					const addOn = addOns.find(a => a.id === line.id);
					if (!addOn) return null;
					return {
						...addOn,
						quantity: line.quantity,
						lineTotal: finite(addOn.price) * line.quantity,
					};
				})
				.filter(Boolean),
		[addOnLines, addOns]
	) as SelectedAddOn[];

	const selectedAddOnIds = useMemo(() => selectedAddOns.map(addOn => addOn.id), [selectedAddOns]);

	/** The payload shape: `{ [addOnId]: quantity }`. */
	const addOnQuantities = useMemo(
		() =>
			selectedAddOns.reduce<Record<number, number>>((map, addOn) => {
				map[addOn.id] = addOn.quantity;
				return map;
			}, {}),
		[selectedAddOns]
	);

	const addOnTotal = useMemo(
		() => selectedAddOns.reduce((total, addOn) => total + addOn.lineTotal, 0),
		[selectedAddOns]
	);

	/**
	 * Everything the deposit and support fee are banded on. Add-ons are part of
	 * what the customer is quoted, so they have to count towards the total the
	 * fee tiers are read with — otherwise a $90 service plus a $40 add-on would
	 * be charged the deposit for a $90 booking.
	 */
	const serviceTotal = serviceOnlyTotal + addOnTotal;

	/**
	 * How long the whole basket takes, add-ons included. The availability call
	 * sends this so the server can drop slots the job would overrun — a two-hour
	 * install used to be offered half an hour before closing.
	 */
	const totalDurationMinutes = useMemo(
		() =>
			costLines.reduce((total, line) => total + line.durationMinutes, 0) +
			selectedAddOns.reduce(
				(total, addOn) => total + finite(addOn.durationMinutes) * addOn.quantity,
				0
			),
		[costLines, selectedAddOns]
	);

	/** Selecting an add-on starts it at one; deselecting forgets its quantity. */
	const toggleAddOn = useCallback((id: number) => {
		setAddOnLines(prev =>
			prev.some(line => line.id === id)
				? prev.filter(line => line.id !== id)
				: [...prev, { id, quantity: ADD_ON_MIN_QUANTITY }]
		);
	}, []);

	// Clamped here rather than only in the stepper so the bounds hold however
	// the quantity is set — the payload can never carry an 11 or a 0.
	const setAddOnQuantity = useCallback((id: number, quantity: number) => {
		const clamped = Math.min(
			ADD_ON_MAX_QUANTITY,
			Math.max(ADD_ON_MIN_QUANTITY, Math.round(quantity))
		);
		setAddOnLines(prev =>
			prev.map(line => (line.id === id ? { ...line, quantity: clamped } : line))
		);
	}, []);

	const clearAddOns = useCallback(() => setAddOnLines([]), []);

	// ── Actions ──
	const toggleService = useCallback((id: number) => {
		setSelectedIds(prev =>
			prev.includes(id) ? prev.filter(existing => existing !== id) : [...prev, id]
		);
	}, []);

	const removeService = useCallback((id: number) => {
		setSelectedIds(prev => prev.filter(existing => existing !== id));
	}, []);

	/**
	 * A pick the customer has to make before the total is settled. A clash
	 * leaves nothing to pick, so that booking goes through with neither field
	 * set and is priced from whatever the stylist confirms.
	 */
	const requiresSize = sizeApplies && !groupClashes;
	const requiresLength = lengthApplies && !groupClashes;

	return {
		categories,
		activeCategory,
		setActiveCategory,
		selectedIds,
		selectedServices,
		toggleService,
		removeService,
		size,
		setSize,
		length,
		setLength,
		sizeOptions,
		lengthOptions,
		sizeApplies,
		lengthApplies,
		requiresSize,
		requiresLength,
		groupClashes,
		costLines,
		selectedAddOnIds,
		selectedAddOns,
		addOnQuantities,
		toggleAddOn,
		setAddOnQuantity,
		clearAddOns,
		addOnTotal,
		serviceOnlyTotal,
		serviceTotal,
		totalDurationMinutes,
	};
};

export default usePublicBookingSelection;
